import { action, computed, makeObservable, observable, override, runInAction } from 'mobx'
import LoadableStore, { LoadingState } from '@migrate/stores/LoadableStore'
import { AnalyticsConfig } from '@portal/pkg-api/gen/shared'
import { AnalyticsApi } from '@portal/pkg-api/shared'

export default class AnalyticsStore extends LoadableStore {
  @observable public analyticsConfig?: AnalyticsConfig

  constructor() {
    super()
    makeObservable(this)
  }

  @computed
  get googleAnalytics() {
    return this.analyticsConfig && this.analyticsConfig.googleAnalytics
  }

  @computed
  get googleAnalyticsKey() {
    return this.googleAnalytics && this.googleAnalytics.key
  }

  @computed
  get pendoUser() {
    return this.analyticsConfig && this.analyticsConfig.pendo
  }

  @computed
  get pendoEnabled() {
    return this.pendoUser && this.pendoUser.enabled
  }

  @computed
  get pendoInitConfig() {
    return (
      this.pendoUser && {
        apiKey: this.pendoUser.apiKey,
        visitor: { ...this.pendoUser.visitor },
        account: { ...this.pendoUser.account },
      }
    )
  }

  @action
  public async load() {
    this.state = LoadingState.LOADING
    if (process.env.NODE_ENV !== 'development') {
      const response = await AnalyticsApi.getAnalyticsConfig()
      runInAction(() => {
        this.analyticsConfig = response.data
      })

      try {
        await Promise.all([this.initPendo()])
      } catch {
        console.log('load 3rd party analytics failed')
      }
    }
    runInAction(() => {
      this.state = LoadingState.LOADED
    })
  }

  @override
  async unload(): Promise<void> {
    this.analyticsConfig = undefined
    await super.unload()
  }

  private async initPendo() {
    if (this.pendoEnabled && this.pendoUser && this.pendoUser.pendoUrl) {
      await this.injectScript(this.pendoUser.pendoUrl)
      // @ts-expect-error pendo is defined by the script injected above and has no global declaration
      pendo.initialize(this.pendoInitConfig)
    }
  }

  private injectScript(src: string, async: boolean = false) {
    return new Promise((res, rej) => {
      const script = document.createElement('script')
      script.src = src
      script.async = async
      script.addEventListener('load', res.bind(null))
      script.addEventListener('error', rej.bind(null))
      document.body.appendChild(script)
    })
  }

  public static STORE_NAME = 'analyticsStore'
  get storeName() {
    return AnalyticsStore.STORE_NAME
  }
}
